// src/constants/useSessionActivity.js
import { useEffect, useRef } from "react";
import api from "./api";

const PING_INTERVAL_MS = 4 * 60 * 1000;  
const ACTIVE_WINDOW_MS = 10 * 60 * 1000; 
const CHECK_TICK_MS = 15 * 1000;        

export default function useSessionActivity() {
  const lastActivityRef = useRef(Date.now());
  const lastPingRef = useRef(0);
  const bcRef = useRef(null);
  const isLeaderRef = useRef(false);
  const storageKey = "wrtk-activity-leader";
  const storagePingKey = "wrtk-activity-lastping";

  useEffect(() => {
    const hasBC = typeof window.BroadcastChannel === "function";
    let bcCleanup = () => {};
    let lsCleanup = () => {};

    if (hasBC) {
      const bc = new BroadcastChannel("wrtk-activity");
      bcRef.current = bc;

      const myId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;

      const onMsg = (e) => {
        const data = e?.data || {};
        if (data.type === "who-is-leader?") {
          if (isLeaderRef.current) bc.postMessage({ type: "leader", id: myId });
        } else if (data.type === "leader") {
          // Jemand anders ist Leader → kein Ping von uns
          isLeaderRef.current = false;
        }
      };

      // Wahl auslösen
      bc.postMessage({ type: "who-is-leader?" });
      const electTimeout = setTimeout(() => {
        // Falls niemand geantwortet hat: wir sind Leader
        isLeaderRef.current = true;
        bc.postMessage({ type: "leader", id: myId });
      }, 200);

      bc.addEventListener("message", onMsg);

      bcCleanup = () => {
        clearTimeout(electTimeout);
        bc.removeEventListener("message", onMsg);
        bc.close();
      };
    } else {
      // Fallback: localStorage "Lease"
      const leaseMs = 5000; // 5s Lease
      const myId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;

      const tryAcquire = () => {
        const now = Date.now();
        const raw = localStorage.getItem(storageKey);
        let current = null;
        try { current = JSON.parse(raw); } catch {}
        const holderAge = current ? now - Number(current.ts || 0) : Infinity;
        if (!current || holderAge > leaseMs) {
          localStorage.setItem(storageKey, JSON.stringify({ id: myId, ts: now }));
          // nach einem Tick prüfen, ob wir noch holder sind
          setTimeout(() => {
            const chk = JSON.parse(localStorage.getItem(storageKey) || "{}");
            isLeaderRef.current = chk.id === myId;
          }, 10);
        } else {
          isLeaderRef.current = current.id === myId;
        }
      };

      // initial versuchen Leader zu werden und regelmäßig Lease erneuern
      tryAcquire();
      const leaseIv = setInterval(() => {
        if (isLeaderRef.current) {
          localStorage.setItem(storageKey, JSON.stringify({ id: myId, ts: Date.now() }));
        } else {
          tryAcquire();
        }
      }, 1000);

      // Auf Storage-Events reagieren (anderer Tab übernimmt)
      const onStorage = (e) => {
        if (e.key === storageKey) {
          const now = Date.now();
          const cur = JSON.parse(e.newValue || "{}");
          const isMine = cur.id === myId;
          isLeaderRef.current = isMine;
          // gelegentlich stempeln wir auch den letzten Ping ins Storage (nur informativ)
          if (e.key === storagePingKey) {
            // ignorieren; rein informativ
          }
        }
      };
      window.addEventListener("storage", onStorage);

      lsCleanup = () => {
        clearInterval(leaseIv);
        window.removeEventListener("storage", onStorage);
        // Lease freigeben (optional)
        const cur = JSON.parse(localStorage.getItem(storageKey) || "{}");
        if (cur && cur.id === myId) localStorage.removeItem(storageKey);
      };
    }

    /* ──────────────────────────────────────────────
       Aktivität sammeln
       ────────────────────────────────────────────── */
    const markActivity = () => { lastActivityRef.current = Date.now(); };
    const evs = ["click", "keydown", "wheel", "mousemove", "touchstart"];
    evs.forEach((ev) => window.addEventListener(ev, markActivity, { passive: true }));
    document.addEventListener("visibilitychange", markActivity);

    /* ──────────────────────────────────────────────
       Periodischer Check: sichtbar? aktiv? Zeit fällig?
       ────────────────────────────────────────────── */
    let stopped = false;

    const tick = () => {
      if (stopped) return;

      const now = Date.now();
      const visible = document.visibilityState === "visible";
      const activeRecently = now - lastActivityRef.current < ACTIVE_WINDOW_MS;
      const due = now - lastPingRef.current >= PING_INTERVAL_MS;

      if (isLeaderRef.current && visible && activeRecently && due) {
        lastPingRef.current = now;
        // Header x-activity: 1 -> Server verlängert Idle gesichert
        api
          .get("/ping", { headers: { "x-activity": "1" }, withCredentials: true })
          .then(() => {
            try { localStorage.setItem(storagePingKey, String(now)); } catch {}
          })
          .catch(() => { /* still ok – nächster Tick probiert erneut */ });
      }
    };

    const iv = setInterval(tick, CHECK_TICK_MS);

    return () => {
      stopped = true;
      clearInterval(iv);
      evs.forEach((ev) => window.removeEventListener(ev, markActivity));
      document.removeEventListener("visibilitychange", markActivity);
      bcCleanup();
      lsCleanup();
    };
  }, []);
}
